import { ChangeEvent, useState } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Options } from "react-select";
import { toast } from "react-toastify";
import * as yup from "yup";

import { CourseDetailForm } from "./CourseDetailForm";
import {
  EntityCourse,
  useUpdateCourseMutation,
} from "../../../api/courses.generated";
import type { Option } from "../../../components/hookFormComponents/SelectInputField";
import { Optional } from "../../../enums/types";
import { useUpload, useUploadMedia } from "../../../hooks";
import { useGetImage } from "../../../hooks/useGetImage";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { AllRoutes } from "../../route/Route";

type FormValues = {
  courseNameEN: string;
  courseNameDE: string;
  shortDescriptionEN: string;
  shortDescriptionDE: string;
  goalDescriptionEN: string;
  goalDescriptionDE: string;
  descriptionEN: string;
  descriptionDE: string;
  structureEN: string;
  structureDE: string;
  targetGroupEN: string;
  targetGroupDE: string;
  image: string;
  lecturer: string;
  titleImageID: string;
  lecturerID: string;
  status: "cs-active" | "cs-preview" | "cs-draft";
};

type Props = {
  courseData?: EntityCourse;
  refetch: () => void;
  lecturerOptions: Options<Option>;
};

export function CourseEditForm({
  courseData,
  refetch,
  lecturerOptions,
}: Props) {
  const { t } = useTranslation();
  const [updateCourse] = useUpdateCourseMutation();
  const { addFileToFormData } = useUpload();

  const FormSchema = yup.object().shape({
    courseNameEN: yup.string(),
    courseNameDE: yup.string(),
    textEn: yup.string(),
    textDe: yup.string(),
  });
  const enTranslations = courseData?.translations?.find(
    ({ language }) => language === "en"
  );
  const deTranslations = courseData?.translations?.find(
    ({ language }) => language === "de"
  );
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      courseNameEN: enTranslations?.name,
      courseNameDE: deTranslations?.name,
      shortDescriptionEN: enTranslations?.shortDescription,
      shortDescriptionDE: deTranslations?.shortDescription,
      goalDescriptionEN: enTranslations?.goalDescription,
      goalDescriptionDE: deTranslations?.goalDescription,
      descriptionEN: enTranslations?.description,
      descriptionDE: deTranslations?.description,
      structureEN: enTranslations?.structure,
      structureDE: deTranslations?.structure,
      targetGroupEN: enTranslations?.targetGroup,
      targetGroupDE: deTranslations?.targetGroup,
      lecturer: courseData?.lecturer?.fullName,
      titleImageID: courseData?.titleImages?.[0].media?.id,
      lecturerID: courseData?.lecturer?.id,
      status: courseData?.status ?? "cs-active",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const [imageFormData, setImageFormData] = useState<Optional<FormData>>(null);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(imageFormData);
  const { downloadedImage, isImageDownloading } = useGetImage(
    uploadedMediaId || courseData?.titleImages?.[0].media?.id
  );
  const navigate = useNavigate();

  const onSubmit = () => {
    const {
      courseNameDE,
      courseNameEN,
      shortDescriptionDE,
      shortDescriptionEN,
      descriptionDE,
      descriptionEN,
      goalDescriptionDE,
      goalDescriptionEN,
      structureDE,
      structureEN,
      targetGroupDE,
      targetGroupEN,
      lecturerID,
      status,
    } = getValues();
    updateCourse({
      id: courseData?.id || "",
      courseUpdateRequest: {
        translations: [
          {
            language: "en",
            name: courseNameEN,
            description: descriptionEN,
            shortDescription: shortDescriptionEN,
            goalDescription: goalDescriptionEN,
            structure: structureEN,
            targetGroup: targetGroupEN,
          },
          {
            language: "de",
            name: courseNameDE,
            description: descriptionDE,
            shortDescription: shortDescriptionDE,
            goalDescription: goalDescriptionDE,
            structure: structureDE,
            targetGroup: targetGroupDE,
          },
        ],
        titleImageID:
          courseData?.titleImages?.[0]?.media?.image?.id ||
          uploadedMediaId ||
          "",
        lecturerID: lecturerID || "",
        status,
      },
    })
      .unwrap()
      .then(() => {
        refetch();
        navigate(AllRoutes.COURSES_LIST.path);
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.course.edit.failed"));
        }
      });
  };

  const onChangePicture = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setImageFormData(addFileToFormData(e.target.files[0]));
    }
  };

  const isImageLoading = isMediaUploading || isImageDownloading;
  const isSubmitButtonDisabled = isImageLoading || !isValid;
  const defaultLecturerValue: Option = {
    value: courseData?.lecturer?.id || "",
    label: courseData?.lecturer?.fullName || "",
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case "cs-active":
        return t("form.status.active");
      case "cs-preview":
        return t("form.status.preview");
      case "cs-draft":
        return t("form.status.draft");
      default:
        return t("form.status.active");
    }
  };

  const defaultStatusValue: Option = {
    value: courseData?.status || "cs-active",
    label: getStatusLabel(courseData?.status),
  };

  const initialValues = {
    defaultEnValues: enTranslations,
    defaultDeValues: deTranslations,
  };

  return (
    <CourseDetailForm
      control={control}
      errors={errors}
      onChangePicture={onChangePicture}
      handleSubmit={handleSubmit(onSubmit)}
      courseImage={downloadedImage || ""}
      isImageLoading={isImageLoading}
      lecturerOptions={lecturerOptions || []}
      defaultLecturerValue={defaultLecturerValue}
      defaultStatusValue={defaultStatusValue}
      isSubmitButtonDisabled={isSubmitButtonDisabled}
      initialValues={initialValues}
      onSubmit={onSubmit}
    />
  );
}
